import { useQuery, useMutation } from '@tanstack/react-query';

export function useInbound() {
  const { data: inboundItems, isLoading, error } = useQuery({
    queryKey: ['inbound-items'],
    queryFn: async () => {
      const response = await fetch('/graphql', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: `
            query GetInboundItems {
              inboundItems {
                id
                status
                createdAt
                updatedAt
                items {
                  id
                  name
                  quantity
                  condition
                }
              }
            }
          `,
        }),
      });
      const json = await response.json();
      return json.data.inboundItems;
    },
  });

  const { mutate: updateInboundItem } = useMutation({
    mutationFn: async ({ id, status }) => {
      const response = await fetch('/graphql', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: `
            mutation UpdateInboundItem($id: ID!, $status: String!) {
              updateInboundItem(id: $id, status: $status) {
                id
                status
              }
            }
          `,
          variables: {
            id,
            status,
          },
        }),
      });
      const json = await response.json();
      return json.data.updateInboundItem;
    },
  });

  return {
    inboundItems,
    isLoading,
    error,
    updateInboundItem,
  };
}

export function useAssignFlow() {
  const { mutate: assignFlow } = useMutation({
    mutationFn: async ({ itemId, flowId }) => {
      const response = await fetch('/graphql', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: `
            mutation AssignFlow($itemId: ID!, $flowId: ID!) {
              assignFlow(itemId: $itemId, flowId: $flowId) {
                id
                status
                flowId
              }
            }
          `,
          variables: {
            itemId,
            flowId,
          },
        }),
      });
      const json = await response.json();
      return json.data.assignFlow;
    },
  });

  return {
    assignFlow,
  };
}

export function useUpdateInspection() {
  const { mutate: updateInspection } = useMutation({
    mutationFn: async ({ itemId, inspectionData }) => {
      const response = await fetch('/graphql', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: `
            mutation UpdateInspection($itemId: ID!, $inspectionData: InspectionInput!) {
              updateInspection(itemId: $itemId, inspectionData: $inspectionData) {
                id
                status
                inspection {
                  condition
                  notes
                  images
                }
              }
            }
          `,
          variables: {
            itemId,
            inspectionData,
          },
        }),
      });
      const json = await response.json();
      return json.data.updateInspection;
    },
  });

  return {
    updateInspection,
  };
}

export function useInboundItem(id) {
  const { data: inboundItem, isLoading, error } = useQuery({
    queryKey: ['inbound-item', id],
    queryFn: async () => {
      const response = await fetch('/graphql', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: `
            query GetInboundItem($id: ID!) {
              inboundItem(id: $id) {
                id
                status
                createdAt
                updatedAt
                items {
                  id
                  name
                  quantity
                  condition
                  inspection {
                    condition
                    notes
                    images
                  }
                }
              }
            }
          `,
          variables: {
            id,
          },
        }),
      });
      const json = await response.json();
      return json.data.inboundItem;
    },
  });

  return {
    inboundItem,
    isLoading,
    error,
  };
}

export function useInboundMetrics() {
  const { data: metrics, isLoading, error } = useQuery({
    queryKey: ['inbound-metrics'],
    queryFn: async () => {
      const response = await fetch('/graphql', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: `
            query GetInboundMetrics {
              inboundMetrics {
                totalItems
                pendingInspection
                completedInspection
                averageProcessingTime
                itemsByStatus {
                  status
                  count
                }
              }
            }
          `,
        }),
      });
      const json = await response.json();
      return json.data.inboundMetrics;
    },
  });

  return {
    metrics,
    isLoading,
    error,
  };
} 